import { useState, useCallback, useMemo } from "react";
import { runtimeConfig } from "../runtimeConfig";
import {
  useRunDetail,
  useLiveEvents,
  type TestStep,
  type LiveStep,
  type LiveEvent,
} from "../hooks/useTestRuns";

const API_BASE = runtimeConfig.apiBaseUrl;

const STEP_STATUS_COLOR: Record<string, string> = {
  passed: "#22c55e",
  failed: "#ef4444",
  error: "#f59e0b",
  running: "#3b82f6",
  pending: "#6b7280",
  skipped: "#8b5cf6",
};

type AnyStep = TestStep | LiveStep;

// TestStep est un super-ensemble structurel de LiveStep ; on discrimine
// donc via le champ "id" (présent uniquement sur TestStep persistés en DB).
function isPersisted(s: AnyStep): s is TestStep {
  return "id" in s;
}

function stepLabel(s: AnyStep): string {
  const name = s.name ?? s.action;
  return `${s.step_index}. ${name}`;
}

function stepDur(s: AnyStep): string {
  if (!s.duration_ms) return "—";
  if (s.duration_ms < 1000) return `${s.duration_ms}ms`;
  return `${(s.duration_ms / 1000).toFixed(1)}s`;
}

interface Props {
  runId: string;
  onClose: () => void;
}

export default function TestRunViewer({ runId, onClose }: Props) {
  const { detail, loading } = useRunDetail(runId);
  const [liveSteps, setLiveSteps] = useState<LiveStep[]>([]);
  const [liveStatus, setLiveStatus] = useState<string | null>(null);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const isActive = (detail?.status === "pending" || detail?.status === "running");

  const handleLiveEvent = useCallback((e: LiveEvent) => {
    if (e.steps.length > 0) {
      setLiveSteps((prev) => {
        const map = new Map(prev.map((s) => [s.step_index, s]));
        for (const s of e.steps) map.set(s.step_index, s);
        return Array.from(map.values()).sort(
          (a, b) => a.step_index - b.step_index,
        );
      });
    }
    if (e.run_status) setLiveStatus(e.run_status);
  }, []);

  useLiveEvents(runId, handleLiveEvent, isActive);

  // Merge: detail steps (DB) + live steps (SSE), dédupliqués par step_index.
  const steps: AnyStep[] = useMemo(() => {
    const map = new Map<number, AnyStep>();
    for (const s of (detail?.steps ?? [])) map.set(s.step_index, s);
    for (const s of liveSteps) map.set(s.step_index, s);
    return Array.from(map.values()).sort((a, b) => a.step_index - b.step_index);
  }, [detail, liveSteps]);

  const effectiveStatus = liveStatus ?? detail?.status ?? "…";
  const reportUrl = `${API_BASE}/test-runs/${runId}/report?format=html`;

  return (
    <div
      style={{
        background: "#111827",
        border: "1px solid #1f2937",
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #1f2937",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {loading ? "Loading…" : (detail?.manifest_name ?? runId.slice(0, 8))}
        </span>
        <span
          style={{
            background: (STEP_STATUS_COLOR[effectiveStatus] ?? "#6b7280") + "22",
            color: STEP_STATUS_COLOR[effectiveStatus] ?? "#9ca3af",
            borderRadius: 4,
            padding: "2px 8px",
            fontSize: 11,
            fontWeight: 700,
            fontFamily: "monospace",
          }}
        >
          {effectiveStatus.toUpperCase()}
        </span>
        <a
          href={reportUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            background: "#1d4ed8",
            color: "#fff",
            borderRadius: 5,
            padding: "3px 10px",
            fontSize: 12,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          HTML Report
        </a>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "#6b7280",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
            padding: "0 4px",
          }}
        >
          ×
        </button>
      </div>

      {/* Steps timeline */}
      <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
        {loading && steps.length === 0 && (
          <p style={{ color: "#6b7280", fontSize: 13 }}>Loading steps…</p>
        )}
        {!loading && steps.length === 0 && (
          <p style={{ color: "#6b7280", fontSize: 13 }}>No steps yet.</p>
        )}
        {steps.map((s) => {
          const color = STEP_STATUS_COLOR[s.status] ?? "#6b7280";
          const expanded = expandedIdx === s.step_index;
          return (
            <div key={s.step_index} style={{ marginBottom: 6 }}>
              <div
                onClick={() =>
                  setExpandedIdx(expanded ? null : s.step_index)
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 10px",
                  background: "#1f2937",
                  borderLeft: `3px solid ${color}`,
                  borderRadius: 5,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                <span style={{ color, fontSize: 15, lineHeight: 1 }}>
                  {s.status === "passed"
                    ? "✓"
                    : s.status === "failed"
                      ? "✗"
                      : s.status === "error"
                        ? "⚠"
                        : s.status === "running"
                          ? "⟳"
                          : "–"}
                </span>
                <span style={{ flex: 1 }}>{stepLabel(s)}</span>
                <span style={{ color: "#6b7280", fontSize: 11 }}>
                  {stepDur(s)}
                </span>
                <span style={{ color: "#6b7280", fontSize: 11 }}>
                  {expanded ? "▲" : "▼"}
                </span>
              </div>
              {expanded && (
                <div
                  style={{
                    background: "#0f172a",
                    border: "1px solid #1f2937",
                    borderTop: "none",
                    borderRadius: "0 0 5px 5px",
                    padding: "8px 12px",
                    fontSize: 12,
                    color: "#d1d5db",
                  }}
                >
                  <div>
                    <strong>Action:</strong> {s.action}
                  </div>
                  {s.error && (
                    <pre
                      style={{
                        marginTop: 6,
                        color: "#fca5a5",
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        fontSize: 11,
                      }}
                    >
                      {s.error}
                    </pre>
                  )}
                  {isPersisted(s) && s.screenshot && (
                    <div style={{ marginTop: 6, color: "#6b7280" }}>
                      📷 {s.screenshot}
                      &nbsp;
                      <span style={{ fontSize: 11 }}>
                        (screenshot in HTML report)
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
